import type { LDClient } from 'launchdarkly-react-client-sdk';

import type { AuthContextValue } from '@/providers/auth/types';
import {
  buildLDContextFromAuth,
  identifyWithTimeout,
  isAnonymousContext,
} from '@/utils/launch-darkly-helpers';

import type { RouteGuardParams } from './types';

/**
 * One-shot anonymous → identified recovery for route guards.
 *
 * Latch and facility-wait policy live in useLaunchDarklyContext (resolveLDContextLatch).
 * This path only identifies when the atlas user claim is present (buildLDContextFromAuth
 * returns null otherwise) and params already carry the URL facility slug.
 */
export async function identifyUserInLaunchDarkly(
  ldClient: LDClient,
  auth: AuthContextValue,
  params: RouteGuardParams,
  routeLabel: string
): Promise<void> {
  if (!auth.isAuthenticated || auth.isLoading || !isAnonymousContext(ldClient)) {
    return;
  }

  console.log(
    `[${routeLabel}] User is authenticated but LaunchDarkly has anonymous context, attempting identification`
  );

  // Build LaunchDarkly context from auth data
  // We can get basic facility data from params, but full data would require API calls
  const facilityData = {
    organizationId: params.org,
    facilityUrlName: params.agent,
  };

  const ldContext = buildLDContextFromAuth(auth.user, facilityData);

  if (ldContext) {
    console.log(`[${routeLabel}] Identifying user in LaunchDarkly...`);
    const identified = await identifyWithTimeout(ldClient, ldContext, 2000);

    if (identified) {
      console.log(`[${routeLabel}] Successfully identified user in LaunchDarkly`);
    } else {
      console.warn(`[${routeLabel}] Failed to identify user in LaunchDarkly within timeout`);
    }
  }
}
